import { lazy } from 'react';
import logger from './logger';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const LOAD_TIMEOUT_MS = 15000;

const retryLazyLoad = (importFn, retries = MAX_RETRIES) => {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      let timedOut = false;
      const timeoutId = setTimeout(() => {
        timedOut = true;
        logger.error('Lazy load timed out', { timeoutMs: LOAD_TIMEOUT_MS });
        reject(new Error(`Loading chunk timed out after ${LOAD_TIMEOUT_MS / 1000}s. Try refreshing.`));
      }, LOAD_TIMEOUT_MS);

      const attemptImport = (attempt) => {
        if (timedOut) return;
        importFn()
          .then((module) => {
            if (timedOut) return;
            clearTimeout(timeoutId);
            if (!module || !module.default) {
              throw new Error(`Module has no default export`);
            }
            resolve(module);
          })
          .catch((error) => {
            if (timedOut) return;
            const isChunkError = error.name === 'ChunkLoadError' ||
                                (error.message && (error.message.includes('Loading chunk') || error.message.includes('Unexpected token')));
            const isNetworkError = error.message?.includes('Failed to fetch') ||
                                  error.message?.includes('NetworkError') ||
                                  error.name === 'TypeError';

            if (attempt < retries && (isChunkError || isNetworkError)) {
              logger.warn('Lazy load attempt failed, retrying', { attempt, maxRetries: retries, error: error.message });
              setTimeout(() => attemptImport(attempt + 1), RETRY_DELAY * attempt);
            } else {
              clearTimeout(timeoutId);
              logger.error('Lazy load failed after retries', { error: error.message });
              reject(error);
            }
          });
      };
      attemptImport(1);
    });
  });
};

export default retryLazyLoad;
